/* eslint-disable max-lines */
import {
    JsonValue,
    OpcodeFlow,
    OpcodeJsonShape,
    OpcodeStepTemplate,
    JsonOpcodeStep,
    LLMStep,
    RAGStep,
    APIStep,
    MemoryStep,
    SubflowStep,
    ChooseStep,
    ToolStep
} from './types';
import {
    ensureRagStepTree
} from './ragStepWireDefaults';

export const STORAGE_KEY = 'opcode_builder_json_v3';

export const DEFAULT_OPCODE: OpcodeJsonShape = {
    id: '',
    name: '',
    description: '',
    inputs: [],
    definitions: [],
    flows: [],
    steps: [],
    output: {}
};

// Controller defaults are optional in the schema, so templates do not set them by default.
const withCommonControllers = <T extends Record<string, unknown>,>(payload: T) => ({
    ...payload
});

export const makeLLMStepTemplate = (): LLMStep => withCommonControllers({
    id: '',
    kind: 'llm',
    inputs: {
        query: ''
    },
    output_type: 'string',
    stream: false,
    unique_name: '',
    model_type: ''
});

export const makeMemoryStepTemplate = (): MemoryStep => withCommonControllers({
    id: '',
    kind: 'memory',
    turns: 5,
    max_tokens: 2000,
    mode: 'full',
    format: 'block',
    include_ts: true,
    max_list_items: 3
});

export const makeApiStepTemplate = (): APIStep => withCommonControllers({
    id: '',
    kind: 'api',
    api_id: '',
    inputs: {
        body: '',
        headers: '',
        query_params: '',
        path_params: ''
    }
});

export const makeRagRetrieveFilesTemplate = (): RAGStep => withCommonControllers({
    id: '',
    kind: 'rag',
    operation: 'retrieve_files',
    inputs: {
        collection_id: '',
        page_size: '50'
    }
} as RAGStep);

export const makeRagSearchFilesTemplate = (): RAGStep => withCommonControllers({
    id: '',
    kind: 'rag',
    operation: 'search_with_files',
    inputs: {
        collection_id: '',
        query: '',
        top_k: '10',
        top_n: '3',
        use_rerank: 'false'
    }
} as RAGStep);

export const makeRagBasicSearchTemplate = (): RAGStep => withCommonControllers({
    id: '',
    kind: 'rag',
    operation: 'basic_search',
    inputs: {
        collection_id: '',
        query: '',
        top_k: '10',
        top_n: '3',
        use_rerank: 'false'
    }
} as RAGStep);

export const makeSubflowStepTemplate = (): SubflowStep => withCommonControllers({
    id: '',
    kind: 'subflow',
    subflow: {
        ref: ''
    },
    with: {}
});

// A-0008.06 — tool step template (Option 1 refactor 2026-04-26).
// Defaults to opcode_as_tool, whose tool_ref carries only tool_name (no
// configurable access boundary). Switching to mcp in the editor will
// add connection_id to tool_ref.
export const makeToolStepTemplate = (): ToolStep => ({
    id: '',
    kind: 'tool',
    protocol: 'opcode_as_tool',
    tool_ref: {
        tool_name: ''
    },
    inputs: {}
});

export const makeChooseStepTemplate = (): ChooseStep => ({
    id: '',
    kind: 'choose',
    choose: {
        cases: [
            {
                when: '',
                run: {
                    kind: 'step',
                    ref: ''
                }
            }
        ],
        default: {
            kind: 'step',
            ref: ''
        }
    }
});

export const makeFlowTemplate = (): OpcodeFlow => ({
    id: '',
    steps: [],
    output: {}
});

export const stringifyOpcode = (value: OpcodeJsonShape) => JSON.stringify(value, null, 2);

/**
 * Removes x-updated* extension fields from an opcode object.
 * Used by sanitization functions to clean up audit-related extension fields.
 */
const removeXUpdatedFields = (opcode: OpcodeJsonShape): OpcodeJsonShape => {
    const cleaned = {
        ...opcode
    };
    Object.keys(cleaned).forEach(key => {
        if (key.startsWith('x-updated')) {
            delete (
                cleaned as Record<string, JsonValue>
            )[key];
        }
    });
    return cleaned;
};

export const safeParseOpcode = (text: string): { ok: true; value: OpcodeJsonShape } | { ok: false; error: string; line?: number; column?: number } => {
    try {
        return {
            ok: true,
            value: JSON.parse(text) as OpcodeJsonShape
        };
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid JSON';

        // Try to extract line and column from error message
        let line: number | undefined;
        let column: number | undefined;

        if (error instanceof SyntaxError) {
            // JSON.parse error messages sometimes include position info
            // Try to extract from message like "Unexpected token... at position X"
            const positionMatch = message.match(/position (\d+)/i);
            if (positionMatch) {
                const position = parseInt(positionMatch[1], 10);
                // Calculate line and column from position
                const beforeError = text.substring(0, position);
                const lines = beforeError.split('\n');
                line = lines.length;
                column = lines[lines.length - 1].length + 1;
            } else {
                // Alternative: try to match "at line X column Y" pattern
                const lineMatch = message.match(/line (\d+)/i);
                const colMatch = message.match(/column (\d+)/i);
                if (lineMatch) {
                    line = parseInt(lineMatch[1], 10);
                }
                if (colMatch) {
                    column = parseInt(colMatch[1], 10);
                }
            }
        }

        return {
            ok: false,
            error: `Invalid JSON: ${message}`,
            line,
            column
        };
    }
};

export const ensureOpcodeShape = (value: OpcodeJsonShape | JsonValue): OpcodeJsonShape => {
    const opcode = (typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {}) as Partial<OpcodeJsonShape>;
    const ensureArray = <T,>(arr: unknown, fallback: T[]): T[] => (Array.isArray(arr) ? arr as T[] : fallback);
    const ensureObject = (obj: unknown) => (typeof obj === 'object' && obj !== null && !Array.isArray(obj) ? obj : {});
    const ensureString = (val: unknown, fallback: string): string => (typeof val === 'string' ? val : fallback);
    const ensureOptionalString = (val: unknown): string | undefined => (typeof val === 'string' ? val : undefined);
    const ensureOptionalDate = (val: unknown): string | Date | undefined => {
        if (val === null || val === undefined) return undefined;
        if (typeof val === 'string') return val;
        if (val instanceof Date) return val;
        return undefined;
    };

    const normalizedFlows = ensureArray(opcode.flows, []).map(flow => {
        const safeFlow = ensureObject(flow) as Partial<OpcodeFlow>;
        return {
            id: typeof safeFlow.id === 'string' ? safeFlow.id : '',
            steps: ensureArray(safeFlow.steps, []) as OpcodeStepTemplate[],
            output: ensureObject(safeFlow.output) as Record<string, JsonValue>
        };
    });

    // A-0008.03: preserve all V2+ fields verbatim (schema_version, audit,
    // resources_used, engine_capabilities_required, default_step_timeout_ms,
    // outputs[], title, governance_hints, hooks, version, tags, category, ...).
    // Spread first, then ensure-shape the structural ones we care about.
    return {
        ...(opcode as OpcodeJsonShape),
        id: ensureOptionalString(opcode.id),
        _id: ensureOptionalString(opcode._id),
        opcode_id: ensureOptionalString(opcode.opcode_id),
        // V2+ canonical: `title`. Legacy `name` kept for UI components that
        // still bind to it; wire normalizer maps `name` -> `title` on save.
        title: ensureOptionalString((opcode as OpcodeJsonShape).title ?? opcode.name),
        name: ensureString((opcode as OpcodeJsonShape).title ?? opcode.name, ''),
        description: ensureOptionalString(opcode.description),
        inputs: ensureArray(opcode.inputs, []),
        outputs: ensureArray((opcode as OpcodeJsonShape).outputs, []),
        definitions: ensureArray(opcode.definitions, []) as OpcodeStepTemplate[],
        flows: normalizedFlows,
        steps: ensureArray(opcode.steps, []) as OpcodeStepTemplate[],
        output: ensureObject(opcode.output) as Record<string, JsonValue>,
        // V2+ audit nested + legacy flat (kept readable for back-compat).
        audit: (opcode as OpcodeJsonShape).audit,
        created: ensureOptionalDate(opcode.created),
        createdBy: ensureOptionalString(opcode.createdBy),
        updated: ensureOptionalDate(opcode.updated),
        updatedBy: ensureOptionalString(opcode.updatedBy)
    };
};

/**
 * Sanitizes opcode payload for CREATE operation.
 * - Removes id, _id, opcode_id (backend generates these)
 * - Removes updated, updatedBy, and x-updated* fields
 * - Keeps createdBy and created as optional (backend defaults createdBy if omitted)
 * - Ensures name is present (should be validated before calling this function)
 */
/**
 * Cleans up steps before sending to backend.
 * - Removes empty default from choose steps
 */
const sanitizeSteps = (steps: JsonOpcodeStep[]): JsonOpcodeStep[] => {
    return steps.map((step) => {
        let next: JsonOpcodeStep = step;

        if (step.kind === 'llm') {
            const llm = { ...step } as LLMStep;
            const legacyMap: Record<string, LLMStep['output_type']> = {
                int: 'number',
                float: 'number',
                bool: 'string'
            };
            if (llm.output_type && legacyMap[llm.output_type as string]) {
                llm.output_type = legacyMap[llm.output_type as string];
            }
            if (llm.stream && llm.output_type && llm.output_type !== 'string') {
                llm.output_type = 'string';
            }
            next = llm;
        } else if (step.kind === 'choose') {
            const chooseStep = { ...step } as ChooseStep;
            if (chooseStep.choose?.default && !chooseStep.choose.default.ref) {
                const chooseWithoutDefault = { ...chooseStep.choose };
                delete (chooseWithoutDefault as { default?: unknown }).default;
                next = { ...chooseStep, choose: chooseWithoutDefault } as JsonOpcodeStep;
            }
        }

        return ensureRagStepTree(next as unknown as Record<string, unknown>) as JsonOpcodeStep;
    });
};

export const sanitizeOpcodeForCreate = (opcode: OpcodeJsonShape): OpcodeJsonShape => {
    const cleaned = {
        ...opcode
    };

    // Remove fields that backend generates or shouldn't be sent
    delete cleaned.id;
    delete cleaned._id;
    delete cleaned.opcode_id;
    delete cleaned.updated;
    delete cleaned.updatedBy;

    // Clean up steps
    cleaned.steps = sanitizeSteps(cleaned.steps);
    if (cleaned.definitions) {
        cleaned.definitions = sanitizeSteps(cleaned.definitions);
    }
    if (cleaned.flows) {
        cleaned.flows = cleaned.flows.map(f => ({
 ...f,
steps: sanitizeSteps(f.steps)
}));
    }

    return removeXUpdatedFields(cleaned);
};

/**
 * Sanitizes opcode payload for UPDATE operation.
 * - Removes id (backend uses opcodeId from request params)
 * - Removes created and createdBy (backend preserves existing values if omitted)
 * - Removes x-updated* fields
 * - May include _id/opcode_id but backend will overwrite them
 * - Ensures name is present (should be validated before calling this function)
 */
export const sanitizeOpcodeForUpdate = (opcode: OpcodeJsonShape): OpcodeJsonShape => {
    const cleaned = {
        ...opcode
    };

    // Remove id (backend uses opcodeId from request params)
    delete cleaned.id;
    // Remove created/createdBy (backend preserves existing values if omitted)
    delete cleaned.created;
    delete cleaned.createdBy;

    // Clean up steps
    cleaned.steps = sanitizeSteps(cleaned.steps);
    if (cleaned.definitions) {
        cleaned.definitions = sanitizeSteps(cleaned.definitions);
    }
    if (cleaned.flows) {
        cleaned.flows = cleaned.flows.map(f => ({
 ...f,
steps: sanitizeSteps(f.steps)
}));
    }

    return removeXUpdatedFields(cleaned);
};

/**
 * V2+ display order — editable fields first, then informational/auto-populated
 * (schema_version, default_step_timeout_ms, resources_used,
 * engine_capabilities_required, audit) at the bottom so the editor view
 * matches how a human reasons about the opcode.
 */
const V2_DISPLAY_ORDER: readonly string[] = [
    // Identity (editable)
    'opcode_id', 'title', 'description', 'version', 'tags', 'category',
    // Interface (editable)
    'inputs', 'outputs',
    // Procedure (editable)
    'definitions', 'flows', 'steps',
    // Governance / hooks (editable)
    'governance_hints', 'hooks',
    // Informational / auto-populated (read-only in spirit)
    'schema_version', 'default_step_timeout_ms',
    'resources_used', 'engine_capabilities_required',
    'audit',
] as const;

const reorderForDisplay = (opcode: OpcodeJsonShape): Record<string, unknown> => {
    const src = opcode as unknown as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of V2_DISPLAY_ORDER) {
        if (src[key] !== undefined) out[key] = src[key];
    }
    // Carry over any other custom keys (x-extensions, etc.) at the end.
    for (const [k, v] of Object.entries(src)) {
        if (!(k in out) && v !== undefined) out[k] = v;
    }
    return out;
};

/**
 * Strips Mongo-internal IDs and the duplicated legacy flat audit fields
 * (the nested `audit{}` block already carries them). Keeps `opcode_id` so the
 * user can see the canonical identity. Wire normalizer drops anything the
 * Server auto-populates on save (resources_used, engine_capabilities_required,
 * audit), so showing them as informational is safe.
 */
export const stripInternalFieldsForDisplay = (opcode: OpcodeJsonShape): OpcodeJsonShape => {
    const cleaned = {
        ...opcode
    };

    delete cleaned.id;
    delete cleaned._id;
    // Hide legacy flat audit fields — `audit.created/...` is the V2+ source.
    delete cleaned.created;
    delete cleaned.createdBy;
    delete cleaned.updated;
    delete cleaned.updatedBy;
    // Hide legacy `name` when `title` is present (avoid duplicate display).
    if (cleaned.title) {
        delete cleaned.name;
    }
    // Hide legacy `output` map when V2+ `outputs[]` is present.
    if (Array.isArray(cleaned.outputs) && cleaned.outputs.length > 0) {
        delete cleaned.output;
    }

    return reorderForDisplay(cleaned) as OpcodeJsonShape;
};

/**
 * Prepares an opcode string for display by parsing, normalizing, and stripping internal fields.
 * Returns the stringified JSON ready for the editor, or the original string if parsing fails.
 */
export const prepareOpcodeStringForDisplay = (opcodeString: string): string => {
    const parsed = safeParseOpcode(opcodeString);
    if (!parsed.ok) {
        return opcodeString;
    }
    const normalized = ensureOpcodeShape(parsed.value);
    const displayOpcode = stripInternalFieldsForDisplay(normalized);
    return stringifyOpcode(displayOpcode);
};

/**
 * Prepares an opcode object for display by stripping internal fields.
 * Returns the stringified JSON ready for the editor.
 */
export const prepareOpcodeForDisplay = (opcode: OpcodeJsonShape): string => {
    const displayOpcode = stripInternalFieldsForDisplay(opcode);
    return stringifyOpcode(displayOpcode);
};
